import { FileKind, ParameterValue } from '@/designer/designer';
import type { NodeId } from 'web';
import type {
  BaseNodeData,
  Edge,
  IteratorInput,
  IteratorRunMode,
  ListSelectorData,
  MediaIteratorData,
} from '@/types/node';
import type { PrecedingIteratorData } from './flow';

export const DEFAULT_ITERATOR_RUN_MODE: IteratorRunMode = 'parallel';

/**
 * Generic utility to get iterator options for any iterator type.
 * Handles both list selectors (text iterators) and image iterators.
 *
 * @param nodeId - The iterator node's ID
 * @param iteratorData - The iterator node data (ListSelectorData or MediaIteratorData)
 * @param listSelectorOptions - Resolves list selector options via the WASM graph
 * @returns Array of options/items that the iterator will iterate over
 */
export const getIteratorValues = (
  nodeId: NodeId,
  iteratorData: ListSelectorData | MediaIteratorData,
  listSelectorOptions: (nodeId: NodeId) => string[],
): string[] | FileKind[] => {
  if (iteratorData.type === 'list_selector' || iteratorData.type === 'muxv2') {
    return listSelectorOptions(nodeId);
  }

  if (iteratorData.type === 'media_iterator') {
    const mediaData = iteratorData as MediaIteratorData;
    const filesParameter = mediaData.files;
    const parameterValue = filesParameter.data as ParameterValue;
    return parameterValue.value as FileKind[];
  }
  return [];
};

/**
 * Whether any of the iterators has no values. An empty iterator fans out over nothing, so a run
 * fed by it ends without output in every run mode. Blank entries don't count, matching the server's
 * `filterEmptyIteratorValues`.
 */
export const hasEmptyIterator = (
  iterators: PrecedingIteratorData[] | null | undefined,
  listSelectorOptions: (nodeId: NodeId) => string[],
): boolean =>
  !!iterators?.some(({ iteratorNode: { id, data } }) =>
    (getIteratorValues(id, data, listSelectorOptions) as unknown[]).every((value) => value === '' || value == null),
  );

/** Iterators with no incoming edge. A connected iterator is filled by upstream nodes, so it can be empty until they run. */
export const getUnconnectedIterators = (
  iterators: PrecedingIteratorData[] | null | undefined,
  edges: Edge[],
): PrecedingIteratorData[] =>
  iterators?.filter(({ iteratorNode }) => !edges.some((edge) => edge.target === iteratorNode.id)) ?? [];

export const getExplicitIteratorRunMode = (
  iteratorInput?: Partial<Pick<IteratorInput, 'runMode'>> | null,
): IteratorRunMode | undefined => {
  if (iteratorInput?.runMode === 'parallel') {
    return 'parallel';
  }
  if (iteratorInput?.runMode === 'matrix') {
    return 'matrix';
  }
  return undefined;
};

export const getIteratorRunMode = (iteratorInput?: Partial<Pick<IteratorInput, 'runMode'>> | null): IteratorRunMode => {
  return getExplicitIteratorRunMode(iteratorInput) ?? DEFAULT_ITERATOR_RUN_MODE;
};

export const resolveIteratorRunMode = (
  runModes: Array<IteratorRunMode | undefined>,
): { runMode: IteratorRunMode; isInconsistent: boolean } => {
  const distinctRunModes = Array.from(
    new Set(runModes.filter((runMode): runMode is IteratorRunMode => runMode !== undefined)),
  );
  if (distinctRunModes.length > 1) {
    return { runMode: DEFAULT_ITERATOR_RUN_MODE, isInconsistent: true };
  }

  return { runMode: distinctRunModes[0] ?? DEFAULT_ITERATOR_RUN_MODE, isInconsistent: false };
};

export const getIteratorRunCount = (iteratorValueCounts: number[], runMode: IteratorRunMode): number => {
  if (!iteratorValueCounts.length) {
    return 0;
  }

  if (runMode === 'matrix') {
    return iteratorValueCounts.reduce((totalRuns, count) => totalRuns * count, 1);
  }

  return Math.min(...iteratorValueCounts);
};

export const isIterator = (nodeData: BaseNodeData & { isIterator?: boolean }) => {
  return nodeData.isIterator || nodeData.type === 'media_iterator';
};
